/**
 * Classification of every database function executable by an authenticated JWT.
 *
 * This catalog is a security input, not a client export. The native manifest only
 * serializes OWNER_CLIENT_RPC_NAMES. Keeping the other groups here lets the database
 * drift check fail when a migration adds a function without an explicit boundary.
 */

import { APP_BRIDGE_CONNECTION_KEY_RPC_NAMES, APP_BRIDGE_RPC_NAMES } from '../app-bridge/contract.js';

export const NODE_ONLY_RPC_NAMES = [
  'claim_node_enrollment',
  'node_ack_main_command_context_rollover',
  'node_acknowledge_instruction',
  'node_active_runs',
  'node_ai_execution_profile',
  'node_ai_session_cleanup_feed',
  'node_claim_command_center_turn',
  'node_claim_observer_reply',
  'node_claim_waiting_job_check',
  'node_claim_worker_chat_turn',
  'node_current_owner_contract',
  'node_fail_command_center_claim',
  'node_fail_observer_reply',
  'node_fail_worker_chat_turn',
  'node_finish_run',
  'node_launch_catalog',
  'node_open_worker_terminal',
  'node_pending_instructions',
  'node_planner_feed',
  'node_prepare_main_command_context_rollover',
  'node_publish_terminal_output',
  'node_read_management_reports',
  'node_record_main_command_context',
  'node_record_supabase_cli_run',
  'node_release_command_center_claim',
  'node_renew_command_center_claim',
  'node_report_ai_connection_status',
  'node_report_ai_execution_actual',
  'node_report_ai_local_runtime_status',
  'node_report_ai_runtime_capabilities',
  'node_report_ai_session_cleanup',
  'node_report_codex_autonomy_status',
  'node_report_ide_adapters',
  'node_report_plan_failure',
  'node_report_removal',
  'node_report_role_execution',
  'node_report_runtime_state',
  'node_report_runtimes',
  'node_report_supabase_cli_credential',
  'node_report_terminal_input',
  'node_report_update',
  'node_report_usage',
  'node_role_instructions',
  'node_record_department_notes',
  'node_role_route',
  'node_run_brief',
  'node_submit_command_center_claim',
  'node_submit_observer_reply',
  'node_submit_plan',
  'node_submit_plan_evaluation',
  'node_submit_research_evidence',
  'node_submit_waiting_job_check',
  'node_submit_worker_chat_reply',
  'node_supabase_cli_credential',
  'node_supervisor_specialist_evidence',
  'node_task_lane_decide',
  'node_task_lane_feed',
  'node_terminal_closed',
  'node_terminal_feed',
  'node_terminal_input_hold',
  'node_terminal_opened',
  'node_update_plan',
  'node_workspace_folder',
  'publish_report_message',
  'publish_run_progress',
  'record_artifact',
  'report_heartbeat',
] as const;

export const INTERNAL_RPC_NAMES = [
  'read_ai_credential_ciphertext',
  'resolve_offering',
] as const;

/**
 * Authenticated functions that exist but are deliberately not native-owner API.
 * App-bridge RPCs stay on this list: they are a separate contract, not the
 * native owner-client surface. m1 still requires them in the grant allowlist.
 * The connection-key doors are on it too: a signed-in caller may execute them,
 * and they answer only for the key in the request header.
 */
export const UNAVAILABLE_OWNER_RPC_NAMES = [
  ...APP_BRIDGE_RPC_NAMES,
  ...APP_BRIDGE_CONNECTION_KEY_RPC_NAMES,
  'post_chat_message',
  'post_owner_command',
] as const;

/** Private/schema helpers are kept out of every owner-client artifact. */
export const AUTHENTICATED_NON_PUBLIC_FUNCTION_NAMES = [
  'brunel_private.append_connector_event',
  'brunel_private.connector_public_json',
  'brunel_private.member_of',
  'brunel_private.node_principal_of',
] as const;

export type RpcAccessClassification = 'owner_client' | 'node_only' | 'internal' | 'unavailable';
